/**
 * @file api.ts
 * @brief TanStack Query hooks for the Super Admin → Sessions feature.
 *
 * @details
 * Centralises every query/mutation against `/api/admin/sessions/*`.
 * - All inputs validated client-side before transmission.
 * - All responses parsed against Zod schemas before being handed to React.
 * - Mutations invalidate the list query so the table reflects updates.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/client       (apiGet/Post/Put/Delete)
 * - shared/api/endpoints    (URL registry)
 * - features/sessions/schemas
 *
 * @references
 * - BACKEND/src/features/admin/routes.js (sessions endpoints)
 *
 * @author DES
 * @date 26-05-2026
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";
import { apiDelete, apiGet } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import {
  AdminSessionDetailSchema,
  AdminSessionsListResponseSchema,
  type AdminSessionDetail,
  type AdminSessionsListParams,
  type AdminSessionsListResponse,
} from "./schemas";

// ----------------------------------------------------------------------
// Query keys
// Centralised so cache invalidation is consistent across components.
// ----------------------------------------------------------------------
export const sessionsKeys = {
  all: ["admin", "sessions"] as const,
  list: (params: AdminSessionsListParams) =>
    ["admin", "sessions", "list", params] as const,
  detail: (id: string) => ["admin", "sessions", "detail", id] as const,
};

// ----------------------------------------------------------------------
// useSessionsList — paginated, filterable list
// ----------------------------------------------------------------------
function buildListSearchParams(params: AdminSessionsListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set("page", String(params.page));
  if (params.limit) usp.set("limit", String(params.limit));
  if (params.status) usp.set("status", params.status);
  if (params.kind) usp.set("kind", params.kind);
  if (params.company_id) usp.set("company_id", params.company_id);
  if (params.user_id) usp.set("user_id", params.user_id);
  if (params.source_language) usp.set("source_language", params.source_language);
  if (params.target_language) usp.set("target_language", params.target_language);
  if (params.from) usp.set("from", params.from);
  if (params.to) usp.set("to", params.to);
  if (params.search) usp.set("search", params.search);
  if (params.include_deleted) usp.set("include_deleted", "true");
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// ----------------------------------------------------------------------
// Metadata only (B2/W4, DES 22-10-2026): VERBLIZR staff never hold session
// content. These hooks serve the Super Admin pages alone, so whatever the
// server sends, comments, utterances, audio and transcript links are dropped
// here (the live backend still sends them until BACKEND 1fd613a deploys).
// ----------------------------------------------------------------------
function withoutContent<T extends object>(row: T): T {
  return { ...row, notes: null };
}

function parseStaffList(raw: unknown): AdminSessionsListResponse {
  const res = AdminSessionsListResponseSchema.parse(raw);
  return { ...res, sessions: (res.sessions ?? []).map(withoutContent) };
}

async function fetchSessionsPage(
  params: AdminSessionsListParams,
): Promise<AdminSessionsListResponse> {
  const url = `${adminEndpoints.sessions}${buildListSearchParams(params)}`;
  const raw = await apiGet<unknown>(url);
  return parseStaffList(raw);
}

export function useSessionsList(
  params: AdminSessionsListParams,
  options?: Partial<UseQueryOptions<AdminSessionsListResponse>>,
) {
  return useQuery<AdminSessionsListResponse>({
    queryKey: sessionsKeys.list(params),
    queryFn: () => fetchSessionsPage(params),
    placeholderData: (prev) => prev,
    ...options,
  });
}

/**
 * One (company, owner) pair in the grouped list (`?group=company_owner`,
 * DES 01-10-2026). The page builds the company -> user tree from these.
 */
export const SessionsCompanyOwnerGroupSchema = z.object({
  company_id: z.string().nullable(),
  company_name: z.string().nullable(),
  owner_id: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  gender: z.string().nullable(),
  avatar_url: z.string().nullable(),
  count: z.number(),
});
export type SessionsCompanyOwnerGroup = z.infer<typeof SessionsCompanyOwnerGroupSchema>;

/** Company group key; `none` for personal sessions (owner has no company). */
export const sessionsCompanyGroupKey = (companyId: string | null) => companyId ?? "none";
/** Leaf (user) group key: `${companyKey}/${ownerKey}`, `none` for no owner. */
export const sessionsOwnerGroupKey = (companyId: string | null, ownerId: string | null) =>
  `${sessionsCompanyGroupKey(companyId)}/${ownerId ?? "none"}`;

/** The list filters without paging (shared by the grouped calls). */
function filterQuery(params: AdminSessionsListParams): URLSearchParams {
  return new URLSearchParams(
    buildListSearchParams({ ...params, page: undefined, limit: undefined }).replace(/^\?/, ""),
  );
}

/**
 * Every (company, owner) pair with sessions under the current filters, with
 * counts (DES 01-10-2026: "haan superadmin pages pe bhi yehi kar do"), as
 * on company Invoices. One server GROUP BY instead of loading every session.
 */
export function useSessionsGroupSummary(params: AdminSessionsListParams, enabled: boolean) {
  return useQuery({
    queryKey: [...sessionsKeys.all, "groups", filterQuery(params).toString()],
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const usp = filterQuery(params);
      usp.set("group", "company_owner");
      const raw = await apiGet<{ groups?: unknown }>(`${adminEndpoints.sessions}?${usp}`);
      return z.array(SessionsCompanyOwnerGroupSchema).parse(raw.groups ?? []);
    },
  });
}

/** Rows per batch when a group opens or loads more. */
export const SESSION_GROUP_BATCH = 25;

/**
 * The sessions of each OPEN user group (leaf key `company/owner`),
 * `batches` x SESSION_GROUP_BATCH, under the same filters
 * (`?company=&owner=`). The route does not cap `limit`, so one request asks
 * for every loaded batch.
 */
export function useSessionsGroupRows(
  params: AdminSessionsListParams,
  open: { key: string; batches: number }[],
) {
  return useQueries({
    queries: open.map(({ key, batches }) => ({
      queryKey: [...sessionsKeys.all, "group", key, batches, filterQuery(params).toString()],
      placeholderData: (prev: AdminSessionsListResponse | undefined) => prev,
      queryFn: async (): Promise<AdminSessionsListResponse> => {
        const [company = "none", owner = "none"] = key.split("/");
        const usp = filterQuery(params);
        usp.set("company", company);
        usp.set("owner", owner);
        usp.set("page", "1");
        usp.set("limit", String(batches * SESSION_GROUP_BATCH));
        const raw = await apiGet<unknown>(`${adminEndpoints.sessions}?${usp}`);
        return parseStaffList(raw);
      },
    })),
  });
}

// ----------------------------------------------------------------------
// useSession — single session by public `session_id`
// ----------------------------------------------------------------------
export function useSession(id: string | null, enabled = true) {
  return useQuery<AdminSessionDetail>({
    queryKey: sessionsKeys.detail(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.sessionById(id!));
      const d = AdminSessionDetailSchema.parse(raw);
      // Counts survive an old backend: work them out before dropping content.
      return {
        ...withoutContent(d),
        utterance_count: d.utterance_count ?? d.utterances.length,
        has_audio:
          d.has_audio ??
          (Boolean(d.audio_url) || d.utterances.some((u) => u.src_audio_url || u.tgt_audio_url)),
        has_transcript: d.has_transcript ?? Boolean(d.transcript_url),
        utterances: [],
        audio_url: null,
        transcript_url: null,
        content_hidden: true,
      };
    },
  });
}

// ----------------------------------------------------------------------
// Mutations — three destructive variants that mirror the mobile app's
// long-press menu sub-options:
//   • useDeleteSession           → DELETE /api/admin/sessions/:id
//     (full cleanup: row + entire GCS folder)
//   • useDeleteSessionAudio      → DELETE /api/admin/sessions/:id/audio
//     (audio assets only, transcript + invoice preserved)
//   • useDeleteSessionArtifacts  → DELETE /api/admin/sessions/:id/artifacts
//     (transcript + invoice PDFs only, audio + invoice row preserved)
// All three invalidate both the list and the open detail query so the
// table + dialog refresh in lockstep without a manual refetch.
// DES Updated: (26-05-2026) — added per-asset variants.
// ----------------------------------------------------------------------
function makeDeleteMutation(
  url: (id: string) => string,
): () => ReturnType<typeof useMutation<unknown, Error, string>> {
  return function useDelete() {
    const qc = useQueryClient();
    return useMutation<unknown, Error, string>({
      mutationFn: async (id) => apiDelete<unknown>(url(id)),
      onSuccess: (_, id) => {
        void qc.invalidateQueries({ queryKey: sessionsKeys.all });
        if (id) {
          void qc.invalidateQueries({ queryKey: sessionsKeys.detail(id) });
        }
      },
    });
  };
}

export const useDeleteSession = makeDeleteMutation(adminEndpoints.sessionById);
export const useDeleteSessionAudio = makeDeleteMutation(
  adminEndpoints.sessionAudio,
);
export const useDeleteSessionArtifacts = makeDeleteMutation(
  adminEndpoints.sessionArtifacts,
);
